import { getExtension, getExtensionValue, isEmpty } from "@medplum/core";
import {
  CodeableConcept,
  Coding,
  Extension,
  Identifier,
  Patient,
  Practitioner,
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
  Reference,
} from "@medplum/fhirtypes";

import { format, formatISO, parse, parseISO } from "date-fns";
import isArray from "lodash/isArray";
import isNull from "lodash/isNull";
import isUndefined from "lodash/isUndefined";
import omit from "lodash/omit";
import pick from "lodash/pick";
import { z, ZodType } from "zod";

import { Extensions } from "@/fhir/enums/extensions";
import { QuestionnaireItemType } from "@/fhir/enums/questionnaire-item-type";
import { FormsLibrarySection, QuiltedHealthCodeSystems } from "@/fhir/enums/quilted-health";
import { getQuestionnaireTypeIdentifier } from "@/fhir/resource-helpers/questionnaire-type-identifier";
import {
  zNumberOptional,
  zSignatureInitialsRequired,
  zStringReq,
  zTransformISODate,
} from "@/utils/zod";

import { usDateFormat, usDateTimeFormat } from "./datetime";
import { getCodeableConceptCode, joinReferenceString } from "./medplum";
import { pascalCase } from "./strings";

export type GenericFormValues = Record<string, Record<string, unknown>>;

export type ExtensionFilter<T = string> = {
  propName: string;
  value: T;
};

export type QuestionnaireSubSections = Record<string, string[]>;

export type QuestionnaireFormValues = z.infer<ReturnType<typeof getQuestionnaireSchema>>;

/**
 * Flowsheet signature questions all carry this fragment in their linkId. Matching is
 * case-insensitive because Labor Vitals names its question `signatureInitials`, while every
 * other flowsheet prefixes it (`fhrSignatureInitials`, `placentaSignatureInitials`, ...).
 */
const SIGNATURE_INITIALS_LINK_ID_FRAGMENT = "signatureinitials";

export const isSignatureInitialsLinkId = (linkId: string | undefined) =>
  !!linkId?.toLowerCase().includes(SIGNATURE_INITIALS_LINK_ID_FRAGMENT);

// TODO: Document proper formset config and usage
const DISPLAY_SUFFIX = "FormsetDisplay";
const CODE_SUFFIX = "FormsetCode";
const IDENTIFIER_SUFFIX = "FormsetIdentifier";

export const getFormsetIdentifierLinkId = (
  items: QuestionnaireItem[] | QuestionnaireResponseItem[] | undefined,
) => {
  const item = items?.find(({ linkId }) => linkId.includes(IDENTIFIER_SUFFIX));
  return item?.linkId;
};

export const getFormsetCodeLinkId = (
  items: QuestionnaireItem[] | QuestionnaireResponseItem[] | undefined,
) => {
  const item = items?.find(({ linkId }) => linkId.includes(CODE_SUFFIX));
  return item?.linkId;
};

export const getFormsetDisplayLinkId = (
  items: QuestionnaireItem[] | QuestionnaireResponseItem[] | undefined,
) => {
  const item = items?.find(({ linkId }) => linkId.includes(DISPLAY_SUFFIX));
  return item?.linkId;
};

export function getQuestionnaireAnswerValue(
  items: QuestionnaireResponseItemAnswer[] | undefined,
  position: number = 0,
): string | boolean | number | Coding {
  const ANSWER_TYPES = [
    "valueBoolean",
    "valueDecimal",
    "valueInteger",
    "valueDate",
    "valueDateTime",
    "valueTime",
    "valueString",
    "valueUri",
    "valueAttachment",
    "valueQuantity",
    "valueReference",
    "valueCoding",
  ] as const;

  const item = items?.[position];
  if (!item) {
    return "";
  }

  const availableKey = ANSWER_TYPES.find((key) => !isEmpty(item[key]));
  if (!availableKey) {
    return "";
  }

  let result: string | number | boolean | Coding;

  if (availableKey === "valueDate") {
    result = usDateFormat(item[availableKey]!);
  } else if (availableKey === "valueDateTime") {
    result = usDateTimeFormat(item[availableKey]!);
  } else if (availableKey === "valueCoding") {
    result = item[availableKey] ?? {};
  } else if (availableKey === "valueBoolean") {
    result = item[availableKey] ?? false;
  } else if (availableKey === "valueAttachment") {
    result = item[availableKey]?.data ?? "";
  } else if (availableKey === "valueDecimal") {
    result = item[availableKey] ?? 0;
  } else if (availableKey === "valueReference") {
    result = item[availableKey]?.display ?? item[availableKey]?.reference ?? "";
  } else {
    result = item[availableKey]?.toString() ?? "";
  }

  return result;
}

export const getSectionedQuestionnaireItems = <T>(
  items: QuestionnaireItem[] | undefined,
  handler: (item: QuestionnaireItem) => T,
  subSection: QuestionnaireItem = { linkId: "", text: "", type: "group" },
  showHidden: boolean = false,
): [
  Record<string, T>,
  QuestionnaireSubSections,
  Record<string, string>,
  Record<string, boolean>,
] => {
  let subSections: QuestionnaireSubSections = {};
  let titles: Record<string, string> = {};
  let questions: Record<string, T> = {};
  let formsetQuestions: Record<string, boolean> = {};

  if (!items) {
    return [questions, subSections, titles, formsetQuestions];
  }

  for (const item of items) {
    if (!item?.linkId) continue;
    if (!showHidden && getExtensionValue(item, Extensions.QuestionnaireItemHidden)) {
      continue;
    }
    if (item?.type === "group") {
      const [groupedQuestions, groupedSubSections, groupedTitles, groupedFormsetQuestions] =
        getSectionedQuestionnaireItems(item.item, handler, item);
      questions = { ...questions, ...groupedQuestions };
      subSections = { ...subSections, ...groupedSubSections };
      titles = { ...titles, ...groupedTitles };
      formsetQuestions = { ...formsetQuestions, ...groupedFormsetQuestions };
      continue;
    }
    questions = { ...questions, [item.linkId]: handler(item) };

    subSections = {
      ...subSections,
      [subSection.linkId]: [...(subSections[subSection.linkId] ?? []), item.linkId],
    };

    /*
    TODO[ClientHistory]: Check if the question has a `formset item` extension.
                         This is temporary solution until we have a robust solution for formset questions.
    */
    formsetQuestions = {
      ...formsetQuestions,
      [subSection.linkId]: !!getExtension(subSection, Extensions.QuestionnaireItemFormset) || false,
    };

    titles = {
      ...titles,
      [subSection.linkId]: subSection.text || "",
    };
  }
  return [questions, subSections, titles, formsetQuestions];
};

export function getLinkIdsWithExtension(
  questionnaireResponse: QuestionnaireResponse | undefined,
  extensionUrl: string,
  filter?: ExtensionFilter<boolean>,
): string[] {
  const result: string[] = [];

  function searchQuestionnaireResponseItemsWithExtension(items?: QuestionnaireResponseItem[]) {
    if (!items) return;

    for (const item of items) {
      if (filter) {
        if (
          item.extension &&
          item.extension.some(
            (ext) =>
              ext.url === extensionUrl &&
              ext[filter?.propName as keyof Extension] === filter?.value,
          )
        ) {
          result.push(item.linkId);
        }
      } else {
        if (item.extension && item.extension.some((ext) => ext.url === extensionUrl)) {
          result.push(item.linkId);
        }
      }

      if (item.item) {
        searchQuestionnaireResponseItemsWithExtension(item.item);
      }
    }
  }

  searchQuestionnaireResponseItemsWithExtension(questionnaireResponse?.item);
  return result;
}

/**
 * Builds a Zod schema from Questionnaire items. Validation is driven by each item's `type` and
 * `required` flag, with one rule that the Questionnaire cannot express:
 *
 * **Signature questions** (linkId containing `SignatureInitials`, see
 * {@link isSignatureInitialsLinkId}) always require at least one signer, whatever `required` says
 * — an entry has to record who made it. Hidden signature questions are exempt, because the full
 * assessments hide each section's copy and fill it from one assessment-wide field that is
 * validated on its own. To keep that rule honest, seed forms should not set `required` on
 * signature items; it has no effect either way.
 */
export const getQuestionnaireSchema = (questionnaireItems: QuestionnaireItem[]) => {
  const schemaObject: Record<string, ZodType<unknown>> = questionnaireItems.reduce(
    (result, item) => {
      const isNestedFormset = !!(
        getExtensionValue(item, Extensions.QuestionnaireItemControlledFormset) &&
        getExtensionValue(item, Extensions.QuestionnaireItemFormset)
      );
      return {
        ...result,
        [item.linkId]: zodValidationOptions(
          (item?.type ?? "string") as QuestionnaireItemType,
          !!item.required,
          item.repeats,
          isNestedFormset,
          item.item,
          item,
        ),
      };
    },
    {} as Record<string, ZodType<unknown>>,
  );

  return z.object(schemaObject);
};

export function getQuestionnaireContext(questionnaire: Questionnaire): string {
  const context = getCodeableConceptCode(
    questionnaire.useContext?.find(
      (context) => context.code.system === QuiltedHealthCodeSystems.FormsLibraryContext,
    )?.valueCodeableConcept,
  );

  return FormsLibrarySection[pascalCase(context) as keyof typeof FormsLibrarySection]; // Ref: https://stackoverflow.com/questions/50417254/dynamically-access-enum-in-typescript-by-key
}

const zodValidationOptions = (
  type: QuestionnaireItemType,
  isRequired: boolean,
  repeats: boolean | undefined,
  isNestedFormset: boolean | undefined,
  nestedItems?: QuestionnaireItem[],
  questionnaireItem?: QuestionnaireItem,
): ZodType<unknown> => {
  if (type === QuestionnaireItemType.group && !isEmpty(nestedItems)) {
    const nestedSchemaObject = nestedItems!.reduce(
      (result, item) => {
        if (!item?.linkId) return result;
        const isNestedFormsetValue = !!(
          getExtensionValue(item, Extensions.QuestionnaireItemControlledFormset) &&
          getExtensionValue(item, Extensions.QuestionnaireItemFormset)
        );

        return {
          ...result,
          [item.linkId]: zodValidationOptions(
            (item?.type ?? "string") as QuestionnaireItemType,
            !!item.required,
            item.repeats,
            isNestedFormsetValue,
            item.item,
            item,
          ),
        };
      },
      {} as Record<string, ZodType<unknown>>,
    );

    if (repeats && isNestedFormset) {
      return z.record(z.string(), z.record(z.string(), z.object(nestedSchemaObject)));
    }
    if (repeats) {
      return z.record(z.string(), z.object(nestedSchemaObject)).default({}).optional();
    }
    return z.object(nestedSchemaObject);
  }

  // Signature questions hold an array of practitioner ids and always need at least one signer,
  // regardless of the `required` flag on the Questionnaire. Hidden ones are exempt: the full
  // assessments hide each section's signature and fill it from a single assessment-wide field,
  // so the user never edits the per-section question and it is validated at that shared field.
  if (isSignatureInitialsLinkId(questionnaireItem?.linkId)) {
    const isHidden = !!getExtensionValue(questionnaireItem, Extensions.QuestionnaireItemHidden);
    return isHidden ? z.any() : zSignatureInitialsRequired();
  }

  if (type === QuestionnaireItemType.string) {
    // Check if this is a phone number field
    // Exclude formset code fields from phone validation
    const isFormsetCodeField = questionnaireItem?.linkId?.toLowerCase().includes("formsetcode");
    const isPhoneField =
      !isFormsetCodeField &&
      (questionnaireItem?.linkId?.toLowerCase().includes("phone") ||
        questionnaireItem?.extension?.some(
          (ext) =>
            ext.url === Extensions.QuestionnaireFieldMask && ext.valueString?.includes("000-0000"),
        ));

    if (isPhoneField) {
      const phoneValidation = z.string().refine(
        (phone) => {
          if (!phone || phone.trim() === "") {
            return true; // Allow empty phone numbers
          }
          // Remove all non-digit characters and check if it's exactly 10 digits
          const digitsOnly = phone.replace(/\D/g, "");
          return digitsOnly.length === 10;
        },
        { message: "Phone number must be exactly 10 digits" },
      );

      return isRequired ? phoneValidation : phoneValidation.optional().nullable();
    }

    // Formset code fields are hidden and must stay string; coerce if choice/coding object is sent by mistake
    if (isFormsetCodeField) {
      const formsetCodeSchema = z.union([
        z.string(),
        z
          .object({ code: z.string().optional(), display: z.string().optional() })
          .passthrough()
          .transform(
            (v) => (v as { code?: string }).code ?? (v as { display?: string }).display ?? "",
          ),
        z.record(z.string(), z.any()).transform((v) => String((v as { code?: string }).code ?? "")),
      ]);
      return isRequired ? formsetCodeSchema : formsetCodeSchema.optional().nullable();
    }

    return isRequired ? zStringReq() : z.string().optional().nullable();
  }
  if (type === QuestionnaireItemType.date) {
    // Mantine v8 DateInput.onChange returns ISO date strings ("YYYY-MM-DD") instead of
    // Date objects. Coerce ISO strings to Date before the schema validates them.
    const coerceDateArg = (arg: unknown): unknown => {
      if (arg === "" || arg === undefined) return null;
      if (typeof arg === "string") return parseISO(arg);
      return arg;
    };
    return isRequired
      ? z
          .preprocess(coerceDateArg, z.date({ message: "This field is required" }))
          .transform(zTransformISODate)
      : z
          .preprocess(coerceDateArg, z.union([z.date(), z.null()]))
          .optional()
          .transform((val) => zTransformISODate(val ?? undefined));
  }
  if (type === QuestionnaireItemType.choice) {
    return isRequired
      ? z.union([
          z.record(z.string(), z.any()).refine((val) => !isEmpty(val), {
            message: "This field is required",
          }),
          z
            .string()
            .nullable()
            .refine((val) => val !== null && val.trim() !== "", {
              message: "This field is required",
            }),
        ])
      : z.any();
  }
  if (type === QuestionnaireItemType.integer) {
    return zNumberOptional();
  }
  // Validation only requires url; .passthrough() allows other FHIR Attachment fields from uploads.
  // sanitizeAttachmentForResponse whitelists all allowed fields when building the response.
  if (type === QuestionnaireItemType.attachment) {
    return isRequired
      ? z
          .object({ url: z.string() })
          .passthrough()
          .refine((val) => val?.url, { message: "This field is required" })
      : z.object({ url: z.string().optional() }).passthrough().nullable().optional();
  }

  return z.any();
};

export function getQuestionnaireResponseFromFormValues(
  questionnaire: Questionnaire,
  values: Record<string, unknown>,
  /** Omitted for org-scoped responses (practice settings), which must not carry a subject. */
  subject: Reference<Patient> | undefined,
  source: Reference<Patient | Practitioner>,
  questionnaireResponse: QuestionnaireResponse | null = null,
  identifier: Identifier | null = null,
  targetLinkId: string | null = null,
): QuestionnaireResponse {
  const targetItem = targetLinkId
    ? questionnaire.item?.find((item) => item.linkId === targetLinkId)
    : undefined;

  let item;
  if (targetItem) {
    const responsesByLinkId = (questionnaireResponse?.item?.reduce(
      (result, item) => ({ ...result, [item.linkId]: item }),
      {},
    ) ?? {}) as Record<string, QuestionnaireResponseItem>;
    item = questionnaire?.item
      ?.map((item) => {
        const questionnaireResponseItem =
          responsesByLinkId[item.linkId] ?? getQuestionnaireResponseItemFromQuestionnaireItem(item);
        if (item.linkId !== targetLinkId) {
          return questionnaireResponseItem;
        }
        return {
          ...questionnaireResponseItem,
          item: mapQuestionnaireItems(targetItem.item, values, questionnaireResponseItem.item),
        };
      })
      .filter(Boolean) as QuestionnaireResponseItem[];
  } else {
    item = mapQuestionnaireItems(questionnaire.item, values, questionnaireResponse?.item);
  }

  const responseIdentifier =
    identifier ??
    questionnaireResponse?.identifier ??
    getQuestionnaireTypeIdentifier(questionnaire);

  return {
    ...(questionnaireResponse || {}),
    ...(responseIdentifier && { identifier: responseIdentifier }),
    resourceType: "QuestionnaireResponse",
    questionnaire: joinReferenceString("Questionnaire", questionnaire.id!),
    status: "in-progress",
    authored: formatISO(new Date()),
    author: source,
    subject,
    source,
    item,
    extension: questionnaireResponse?.extension || [
      {
        url: Extensions.QuestionnaireResponseNamedVersion,
        valueString: "v0",
      },
      {
        url: Extensions.QuestionnaireResponseIsCompleted,
        valueBoolean: false,
      },
    ],
  };
}

const getQuestionnaireResponseItemFromQuestionnaireItem = (
  questionnaireItem: QuestionnaireItem | undefined,
): QuestionnaireResponseItem => {
  return pick(questionnaireItem ?? ({} as QuestionnaireItem), [
    "id",
    "linkId",
    "text",
    "extension",
    "modifierExtension",
    "definition",
  ]);
};

const convertToQuestionnaireResponseItems = (
  questionnaireItem: QuestionnaireItem | undefined,
  questionnaireResponseItems: QuestionnaireResponseItem[] | undefined = undefined,
): QuestionnaireResponseItem[] => {
  const partialQuestionnaireResponseItem =
    getQuestionnaireResponseItemFromQuestionnaireItem(questionnaireItem);
  const result =
    questionnaireResponseItems?.map((questionnaireResponseItem) => ({
      ...partialQuestionnaireResponseItem,
      ...(questionnaireResponseItem ?? {}),
    })) ?? [];

  return result.length ? result : [partialQuestionnaireResponseItem];
};

const handleFormsetAnswers = (
  result: QuestionnaireResponseItem[],
  nestedValues: Record<string, unknown> | undefined,
  questionnaireItem: QuestionnaireItem,
  questionnaireResponseItems: QuestionnaireResponseItem[],
  matchingPreviousQuestionnaireResponseItems: QuestionnaireResponseItem[] | undefined,
) => {
  const entries =
    nestedValues === undefined
      ? []
      : Object.entries(nestedValues).sort(([a], [b]) => Number(a) - Number(b));
  const parsedValues = entries.map(([, v]) => v);

  if (isEmpty(parsedValues) && !isEmpty(questionnaireResponseItems) && nestedValues === undefined) {
    result.push(...questionnaireResponseItems);
    return;
  }

  for (const [index, value] of parsedValues.entries()) {
    const questionnaireResponseItem =
      questionnaireResponseItems[index] ?? omit(questionnaireResponseItems[0], "answer");

    const previousQuestionnaireResponseItem = matchingPreviousQuestionnaireResponseItems?.[index];

    const isNestedFormset = !!(
      getExtensionValue(questionnaireItem, Extensions.QuestionnaireItemControlledFormset) &&
      getExtensionValue(questionnaireItem, Extensions.QuestionnaireItemFormset)
    );

    if (isNestedFormset) {
      for (const item of Object.values(value as GenericFormValues)) {
        const mappedItems = mapQuestionnaireItems(
          questionnaireItem.item!,
          item as Record<string, unknown>,
          previousQuestionnaireResponseItem?.item,
        );
        if (mappedItems.some(responseItemHasAnswer)) {
          result.push({
            ...questionnaireResponseItem,
            item: mappedItems,
          });
        }
      }
    } else {
      const mappedItems = mapQuestionnaireItems(
        questionnaireItem.item!,
        value as Record<string, unknown>,
        previousQuestionnaireResponseItem?.item,
      );
      if (mappedItems.some(responseItemHasAnswer)) {
        result.push({
          ...questionnaireResponseItem,
          item: mappedItems,
        });
      }
    }
  }
};

const ANSWER_VALUE_KEYS = [
  "valueBoolean",
  "valueDecimal",
  "valueInteger",
  "valueDate",
  "valueDateTime",
  "valueTime",
  "valueString",
  "valueUri",
  "valueAttachment",
  "valueQuantity",
  "valueReference",
  "valueCoding",
] as const;

function responseItemHasAnswer(item: QuestionnaireResponseItem): boolean {
  if (item.answer?.length) {
    const hasMeaningful = item.answer.some((a) =>
      ANSWER_VALUE_KEYS.some((key) => !isEmpty((a as Record<string, unknown>)[key])),
    );
    if (hasMeaningful) return true;
  }
  if (item.item?.length) {
    return item.item.some(responseItemHasAnswer);
  }
  return false;
}

function mapQuestionnaireItems(
  items: QuestionnaireItem[] | undefined,
  values: Record<string, unknown>,
  previousQuestionnaireResponseItems: QuestionnaireResponseItem[] | undefined,
): QuestionnaireResponseItem[] {
  const result: QuestionnaireResponseItem[] = [];
  if (!items) return result;

  for (const questionnaireItem of items) {
    if (!questionnaireItem?.linkId) continue;

    const matchingPreviousQuestionnaireResponseItems = previousQuestionnaireResponseItems?.filter(
      (item) => item.linkId === questionnaireItem.linkId,
    );

    const questionnaireResponseItems = convertToQuestionnaireResponseItems(
      questionnaireItem,
      matchingPreviousQuestionnaireResponseItems,
    );

    const nestedValues = values[questionnaireItem.linkId] as Record<string, unknown> | undefined;
    if (questionnaireItem.repeats) {
      handleFormsetAnswers(
        result,
        nestedValues,
        questionnaireItem,
        questionnaireResponseItems,
        matchingPreviousQuestionnaireResponseItems,
      );
      continue;
    }

    // If the item doesn't repeat, then there should be only one answer
    const questionnaireResponseItem = questionnaireResponseItems[0];
    const previousQuestionnaireResponseItem = matchingPreviousQuestionnaireResponseItems?.[0];

    if (questionnaireItem?.type === "group") {
      const groupItems = mapQuestionnaireItems(
        questionnaireItem.item!,
        nestedValues || values,
        previousQuestionnaireResponseItem?.item,
      );
      const groupHasAnswer = groupItems.some(responseItemHasAnswer);
      if (groupHasAnswer) {
        result.push({
          ...questionnaireResponseItem,
          item: groupItems,
        });
      }
      continue;
    }

    const value = values[questionnaireItem.linkId];

    if (value === "") {
      questionnaireResponseItem.answer = [];
      result.push(questionnaireResponseItem);
      continue;
    }

    const isValid = !isUndefined(value) && !isNull(value);
    if (!isValid) {
      if (value === null) {
        questionnaireResponseItem.answer = [];
        result.push(questionnaireResponseItem);
      } else if (previousQuestionnaireResponseItem?.answer) {
        questionnaireResponseItem.answer = previousQuestionnaireResponseItem.answer;
        result.push(questionnaireResponseItem);
      }
      continue;
    }

    const formControlCodeableConcept = getExtensionValue(
      questionnaireItem,
      Extensions.QuestionnaireItemControl,
    ) as CodeableConcept | undefined;
    const formControlType = formControlCodeableConcept?.coding?.[0]?.code;

    const formsetLinkId = getExtensionValue(
      questionnaireItem,
      Extensions.QuestionnaireItemFormsetController,
    );

    if (questionnaireItem.answerValueSet && !formControlType && !formsetLinkId) {
      questionnaireResponseItem.answer = mapAnswerValue(value);
    } else if (formControlType && !questionnaireItem.answerValueSet) {
      questionnaireResponseItem.answer = handleFormControlAnswer(formControlType, value);
    } else if ((formControlType || formsetLinkId) && questionnaireItem.answerValueSet) {
      questionnaireResponseItem.answer = handleFormControlWithValueSet(value as string[]);
    } else {
      questionnaireResponseItem.answer = handleBaseTypeAnswer(
        questionnaireItem?.type ?? "string",
        value,
      );
    }

    const questionsHasScore = questionnaireItem?.answerOption?.some(
      (question) => question.extension,
    );

    if (questionsHasScore) {
      questionnaireResponseItem.extension = updateExtensionsWithScore(
        questionnaireItem,
        questionnaireResponseItem,
      );
    }

    result.push(questionnaireResponseItem);
  }

  return result;
}

function updateExtensionsWithScore(
  questionnaireItem: QuestionnaireItem | undefined,
  questionnaireResponseItem: QuestionnaireResponseItem | undefined = undefined,
): Extension[] {
  const answer = questionnaireResponseItem?.answer?.[0];

  if (!answer) return questionnaireResponseItem?.extension ?? [];

  let code: string | undefined;
  if ("code" in answer) {
    code = (answer as Coding).code;
  } else if ("valueCoding" in answer) {
    code = (answer as { valueCoding: Coding }).valueCoding?.code;
  }

  const questionnaireItemAnswerOption = questionnaireItem?.answerOption?.find(
    (answerOption) => answerOption.valueCoding?.code === code,
  );

  const filteredExtensions = questionnaireResponseItem.extension?.filter(
    (extension) => extension.url !== Extensions.QuestionnaireItemScore,
  );

  const extensions = [
    ...(filteredExtensions ?? []),
    ...(questionnaireItemAnswerOption?.extension ?? []),
  ];

  return extensions;
}

function handleFormControlWithValueSet(value: string[]) {
  let valueArray: string[] | { valueCoding: unknown }[] = value;

  if (!isArray(value)) {
    valueArray = [{ valueCoding: value }];
  }

  const parsedValue = valueArray.map((item) => {
    const parsedItem = typeof item !== "string" ? item : JSON.parse(item);

    if (Object.hasOwnProperty.call(parsedItem, "valueCoding")) {
      return parsedItem;
    }

    return { valueCoding: parsedItem };
  });
  return parsedValue;
}

/**
 * Extracts the answer for a radio-button form control.
 *
 * @param value - The raw value from the form control
 * @returns An array of QuestionnaireResponseItemAnswer based on the provided value.
 */
function getRadioButtonAnswer(value: unknown): QuestionnaireResponseItemAnswer[] {
  if (value && typeof value === "object") {
    if ("valueCoding" in value) {
      return [value as QuestionnaireResponseItemAnswer];
    } else if ("valueString" in value) {
      const stringValue = (value as { valueString: unknown }).valueString;
      if (stringValue && typeof stringValue === "object" && "code" in stringValue) {
        return [{ valueCoding: stringValue as Coding }];
      }
      return [value as QuestionnaireResponseItemAnswer];
    } else if ("code" in value) {
      return [{ valueCoding: value as Coding }];
    } else {
      return [{ valueCoding: value as Coding }];
    }
  } else if (typeof value === "string") {
    return [
      {
        valueCoding: {
          code: value,
          display: value,
        },
      },
    ];
  }
  // Fallback in case value is not an object
  return [{ valueCoding: value as Coding }];
}

function handleFormControlAnswer(
  formControlType: string,
  value: unknown,
): QuestionnaireResponseItemAnswer[] {
  switch (formControlType) {
    case "radio-button":
      return getRadioButtonAnswer(value);
    case "slider":
    case "switch":
      return [{ valueCoding: value as Coding }];
    case "check-box":
      return (value as string[])?.map((answerValue) => ({ valueString: answerValue }));
    case "text-box":
      return [{ valueString: value as string }];
    case "native-select":
      return [{ valueCoding: value as Coding }];
    case "signature-select":
      if (value === undefined || value === null) {
        return [];
      }
      return (Array.isArray(value) ? value : [value]).map((v) => ({ valueString: v }));
    case "number-input":
    case "cumulative-blood-loss":
      return [{ valueDecimal: Number(value) }];
    case "timer":
      return [{ valueTime: value as string }];
    case "timestamp-display":
      if (value === undefined || value === null) {
        return [];
      }
      return [{ valueDateTime: value as string }];
    default:
      return [];
  }
}

/**
 * Ensures attachment shape is valid for QuestionnaireResponse (FHIR Attachment).
 * Whitelists url, contentType, title, creation, data, size, hash (form validation only requires url).
 * Coerces contentType to string to avoid "Element definition not found for boolean contentType".
 */
function sanitizeAttachmentForResponse(
  attachment: QuestionnaireResponseItemAnswer["valueAttachment"],
): QuestionnaireResponseItemAnswer["valueAttachment"] {
  if (!attachment || typeof attachment !== "object") return attachment;
  const out: Record<string, unknown> = {};
  if (attachment.url != null) out.url = attachment.url;
  if (attachment.contentType != null)
    out.contentType =
      typeof attachment.contentType === "string"
        ? attachment.contentType
        : String(attachment.contentType);
  if (attachment.title != null) out.title = attachment.title;
  if (attachment.creation != null) out.creation = attachment.creation;
  if (attachment.data != null) out.data = attachment.data;
  if (attachment.size != null) out.size = attachment.size;
  if (attachment.hash != null) out.hash = attachment.hash;
  return out as QuestionnaireResponseItemAnswer["valueAttachment"];
}

export function handleBaseTypeAnswer(
  itemType: string,
  value: unknown,
): QuestionnaireResponseItemAnswer[] {
  switch (itemType) {
    case "string":
      return [{ valueString: value as string }];
    case "choice":
      if (value && typeof value === "object") {
        if ("valueString" in value) {
          return [value as QuestionnaireResponseItemAnswer];
        } else if ("valueCoding" in value) {
          return [value as QuestionnaireResponseItemAnswer];
        } else if ("code" in value && "system" in value) {
          // If it's a Coding object (has both code and system properties), convert to valueCoding
          return [{ valueCoding: value as Coding }];
        } else {
          return [{ valueString: JSON.stringify(value) }];
        }
      } else {
        return [{ valueString: value as string }];
      }
    case "date":
      return [{ valueDate: value as string }];
    case "reference":
      if (!value || (Array.isArray(value) && value.length === 0)) {
        return [];
      }
      return [{ valueReference: Array.isArray(value) ? value[0] : value }];
    case "integer":
      return [{ valueInteger: value as number }];
    case "decimal":
      return [{ valueDecimal: Number(value) }];
    case "time":
      return [{ valueTime: format(parse(value as string, "HH:mm:ss", new Date()), "HH:mm:ss") }];
    case "dateTime":
      if (value === undefined || value === null) {
        return [];
      }
      return [{ valueDateTime: value as string }];
    case "boolean":
      return [{ valueBoolean: value as boolean }];
    case "attachment":
      if (!value || (typeof value === "object" && !("url" in value))) return [];
      return [
        {
          valueAttachment: sanitizeAttachmentForResponse(
            value as QuestionnaireResponseItemAnswer["valueAttachment"],
          ),
        },
      ];
    default:
      return [];
  }
}

/**
 * Maps a value to a QuestionnaireResponseItemAnswer array based on its type
 */
function mapAnswerValue(value: unknown): QuestionnaireResponseItemAnswer[] {
  if (value && typeof value === "object") {
    if ("valueCoding" in value) {
      return [value as QuestionnaireResponseItemAnswer];
    } else if ("code" in value) {
      return [{ valueCoding: value as Coding }];
    } else {
      return [
        {
          valueCoding: {
            code: String(value),
            display: String(value),
          },
        },
      ];
    }
  } else if (typeof value === "string") {
    return [
      {
        valueCoding: {
          code: value,
          display: value,
        },
      },
    ];
  } else {
    return [{ valueCoding: value as Coding }];
  }
}

export function getFormsetAnswers(
  response: QuestionnaireResponse,
  linkId: string,
  groupByLinkId: string,
): Record<string, Record<string, QuestionnaireResponseItemAnswer>[]> {
  const questionnaireResponseItems = findQuestionnaireResponseItems(response.item, linkId);

  if (!questionnaireResponseItems.length) {
    return {};
  }

  const result: Record<string, Record<string, QuestionnaireResponseItemAnswer>[]> = {};

  // Maps answers by groupByLinkId
  for (const questionnaireResponseItem of questionnaireResponseItems) {
    const answer = extractAnswers(questionnaireResponseItem.item || []);
    const identifier = get([answer[groupByLinkId]]);
    let key;
    if ((identifier as Coding)?.code) {
      key = (identifier as Coding).code?.toString() ?? "";
    } else {
      key = identifier?.toString() ?? "";
    }

    if (!result[key]) {
      result[key] = [];
    }

    result[key].push(answer);
  }

  return result;
}

const findQuestionnaireResponseItems = (
  questionnaireResponseItems: QuestionnaireResponseItem[] | undefined,
  linkId: string,
): QuestionnaireResponseItem[] => {
  const result: QuestionnaireResponseItem[] = [];

  for (const item of questionnaireResponseItems ?? []) {
    if (item.linkId === linkId) {
      result.push(item);
    }
    if (item.item) {
      result.push(...findQuestionnaireResponseItems(item.item, linkId));
    }
  }

  return result;
};

const extractAnswers = (items: QuestionnaireResponseItem[]) => {
  const answers: Record<string, QuestionnaireResponseItemAnswer> = {};

  for (const { linkId, answer, item } of items) {
    if (item) {
      const subGroupAnswers: Record<string, QuestionnaireResponseItemAnswer> = {};
      item.forEach((subItem) => {
        if (subItem.answer) {
          subGroupAnswers[subItem.linkId] = subItem.answer?.[0] ?? {};
        }
      });
      answers[linkId] = subGroupAnswers;
    } else {
      answers[linkId] = answer?.[0] ?? {};
    }
  }

  return answers;
};

export const ANSWER_TYPES = [
  "valueBoolean",
  "valueDecimal",
  "valueInteger",
  "valueDate",
  "valueDateTime",
  "valueTime",
  "valueString",
  "valueUri",
  "valueAttachment",
  "valueQuantity",
  "valueReference",
  "valueCoding",
] as const;

function get(items: QuestionnaireResponseItemAnswer[], position: number = 0) {
  const item = items?.[position];
  if (!item) return;

  const availableKey = ANSWER_TYPES.find((key) => !isEmpty(item[key]));
  if (!availableKey) return;

  return item[availableKey];
}

export type QuestionnaireFormErrors = Record<string, unknown>;

/**
 * Builds a lookup from Mantine form error paths to questionnaire item display text.
 */
export function buildQuestionnaireLabelLookup(
  items: QuestionnaireItem[],
  parentPath = "",
): Record<string, string> {
  const lookup: Record<string, string> = {};

  for (const item of items) {
    if (!item.linkId) continue;

    const path = parentPath ? `${parentPath}.${item.linkId}` : item.linkId;
    const label = item.text?.trim();

    if (label) {
      lookup[item.linkId] = label;
      lookup[path] = label;
    }

    if (item.type === "group" && item.item?.length) {
      Object.assign(lookup, buildQuestionnaireLabelLookup(item.item, path));
    }
  }

  return lookup;
}

/**
 * Resolves human-readable labels for incomplete required questionnaire fields from form errors.
 */
export function getIncompleteQuestionnaireLabels(
  errors: QuestionnaireFormErrors,
  items: QuestionnaireItem[],
): string[] {
  const lookup = buildQuestionnaireLabelLookup(items);
  const labels: string[] = [];
  const seen = new Set<string>();

  for (const errorPath of Object.keys(errors)) {
    const segments = errorPath.split(".");
    const lastSegment = segments[segments.length - 1] ?? errorPath;
    const label = lookup[errorPath] ?? lookup[lastSegment] ?? lastSegment;

    if (!seen.has(label)) {
      seen.add(label);
      labels.push(label);
    }
  }

  return labels;
}

export function getQuestionnaireItemAnswerValue(
  answer: QuestionnaireResponseItemAnswer | undefined,
): string {
  if (!answer) return "";

  const availableKey = ANSWER_TYPES.find((key) => !isEmpty(answer[key]));
  if (!availableKey) return "";

  switch (availableKey) {
    case "valueCoding":
      return answer[availableKey]?.display ?? answer[availableKey]?.code ?? "";
    case "valueBoolean":
      return answer[availableKey] ? "Yes" : "No";
    case "valueDate":
      return usDateFormat(answer[availableKey]);
    case "valueDateTime":
      return usDateTimeFormat(answer[availableKey]);
    case "valueAttachment":
      return answer[availableKey]?.data ?? "";
    case "valueQuantity":
      return answer[availableKey]?.value?.toString() ?? "";
    default:
      return answer[availableKey]?.toString() ?? "";
  }
}
